import { Injectable } from '@angular/core';
import { Photo } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { FaceDetection, ClassificationMode } from '@capacitor-mlkit/face-detection';
import * as faceDetection from '@tensorflow-models/face-detection';
import '@tensorflow/tfjs-core';
import '@tensorflow/tfjs-backend-webgl';
import { LlmService } from 'src/shared/providers/llm.service';
import { photoToImageContent, createTextContent } from 'src/shared/utils/image-utils';

export interface SelfieVerificationResult {
  isValid: boolean;
  reason?: string;
}

/**
 * Service for verifying selfies using LLM analysis
 *
 * Checks if a selfie actually contains a real person in a real setting
 * using Claude AI through the autosuggest API.
 */
@Injectable()
export class SelfieVerificationService {
  private tfDetector: faceDetection.FaceDetector | null = null;

  constructor(private llmService: LlmService) {}

  /**
   * Verify a selfie photo using LLM
   * @param photo Capacitor Photo object from camera
   * @returns Promise with verification result (isValid and optional reason)
   */
  async verifySelfie(photo: Photo): Promise<SelfieVerificationResult> {
    try {
      const systemPrompt =
        'You are a verification system checking if an image is a legitimate selfie of a real person in a real setting. ' +
        'Requirements: The person\'s face must be clearly visible and uncovered (no masks, veils, heavy sunglasses, or other coverings). ' +
        'The person\'s eyes must be open and visible. ' +
        'Look for signs of authenticity: a real person\'s uncovered face with open eyes, genuine lighting, real background. ' +
        'Reject obvious fakes, filters, AI-generated images, screenshots, masked faces, heavily obscured faces, closed eyes, or non-photo content. ' +
        'Be strict but fair.';

      const imageContent = photoToImageContent(photo);
      const textContent = createTextContent(
        'Is this a legitimate selfie of a real person in a real setting? Respond only with valid JSON.'
      );

      const responseSchema = {
        type: 'object',
        properties: {
          isValid: {
            type: 'boolean',
            description: 'Whether the image is a legitimate selfie'
          },
          reason: {
            type: 'string',
            description: 'Brief explanation if invalid (optional)'
          }
        },
        required: ['isValid']
      };

      const request = {
        systemPrompt,
        prompt: [textContent, imageContent],
        responseSchema,
        maxTokens: 200
      };

      const response = await this.llmService.suggest(request);

      // Extract the result from the LLM response
      if (response.result && typeof response.result === 'object') {
        return {
          isValid: response.result.isValid === true,
          reason: response.result.reason
        };
      }

      // If we can't parse the response, default to invalid
      console.warn('Unexpected LLM response format:', response);
      return {
        isValid: false,
        reason: 'Unable to verify image'
      };
    } catch (error) {
      console.error('Selfie verification error:', error);
      // On error, default to invalid to be safe
      return {
        isValid: false,
        reason: 'Verification service error'
      };
    }
  }

  /**
   * Verify a selfie photo using ML Kit Face Detection (on-device)
   * @param photo Capacitor Photo object from camera
   * @returns Promise with verification result (isValid and optional reason)
   */
  async verifySelfieWithMLKit(photo: Photo): Promise<SelfieVerificationResult> {
    let imagePath = photo.path ?? photo.webPath;

    // If no file path but base64 is available, write to a temp file
    if (!imagePath && photo.base64String) {
      const tempFile = await Filesystem.writeFile({
        path: 'selfie-verification.jpeg',
        data: photo.base64String,
        directory: Directory.Cache,
      });
      imagePath = tempFile.uri;
    }

    if (!imagePath) {
      return {
        isValid: false,
        reason: 'No image data provided'
      };
    }

    const result = await FaceDetection.processImage({
      path: imagePath,
      classificationMode: ClassificationMode.All
    });

    if (result.faces.length === 0) {
      return {
        isValid: false,
        reason: 'No face detected in the image'
      };
    }

    if (result.faces.length > 1) {
      return {
        isValid: false,
        reason: 'Multiple faces detected. Please take a selfie with only your face'
      };
    }

    const face = result.faces[0];
    const eyeOpenThreshold = 0.5;

    // Check if eyes are visible (detects sunglasses/closed eyes)
    if (
      face.leftEyeOpenProbability === undefined ||
      face.rightEyeOpenProbability === undefined ||
      face.leftEyeOpenProbability < eyeOpenThreshold ||
      face.rightEyeOpenProbability < eyeOpenThreshold
    ) {
      return {
        isValid: false,
        reason: 'Eyes must be visible. Please remove sunglasses and keep eyes open'
      };
    }

    return {
      isValid: true
    };
  }

  /**
   * Verify a selfie photo using TensorFlow.js Face Detection (web)
   * @param photo Capacitor Photo object from camera
   * @returns Promise with verification result (isValid and optional reason)
   */
  async verifySelfieWithTensorFlow(photo: Photo): Promise<SelfieVerificationResult> {
    if (!photo.webPath && !photo.base64String) {
      return {
        isValid: false,
        reason: 'No image data provided'
      };
    }

    if (!this.tfDetector) {
      this.tfDetector = await faceDetection.createDetector(
        faceDetection.SupportedModels.MediaPipeFaceDetector,
        {
          runtime: 'tfjs'
        }
      );
    }

    const image = await this.loadImage(photo);
    const faces = await this.tfDetector.estimateFaces(image);

    if (faces.length === 0) {
      return {
        isValid: false,
        reason: 'No face detected in the image'
      };
    }

    if (faces.length > 1) {
      return {
        isValid: false,
        reason: 'Multiple faces detected. Please take a selfie with only your face'
      };
    }

    const face = faces[0];
    return {
      isValid: true
    };
  }

  /**
   * Verify a selfie using the appropriate method based on platform
   * Uses ML Kit on native (Android/iOS) and TensorFlow.js on web
   * @param photo Capacitor Photo object from camera
   * @returns Promise with verification result (isValid and optional reason)
   */
  async verifySelfieAuto(photo: Photo): Promise<SelfieVerificationResult> {
    const isNative = Capacitor.isNativePlatform();
    if (isNative) {
      return this.verifySelfieWithMLKit(photo);
    }
    return this.verifySelfieWithTensorFlow(photo);
  }

  private loadImage(photo: Photo): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;

      if (photo.webPath) {
        img.src = photo.webPath;
      } else if (photo.base64String) {
        img.src = `data:image/jpeg;base64,${photo.base64String}`;
      } else {
        reject(new Error('No image source available'));
      }
    });
  }
}
